import { apiRequest, ApiError } from '../../api/http';
import type { CommunityVoice, LandingAdaptiveBlock, LandingData, LandingPlace } from './types';

type Row = Record<string, unknown>;

export interface LandingCity {
  id: string;
  name: string;
  name_local: string;
  country_code: string;
}

export async function loadLandingCities(query = ''): Promise<LandingCity[]> {
  const result = await apiRequest<{ items?: LandingCity[] }>('/locations/cities', {
    query: { country_code: 'TH', q: query },
  });
  return result.items || [];
}

function rows(value: unknown): Row[] {
  return Array.isArray(value) ? value.filter((item): item is Row => Boolean(item) && typeof item === 'object') : [];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function storedReviewSummary(value: unknown): LandingPlace['review_summary'] {
  const summary = value && typeof value === 'object' ? value as Row : {};
  return {
    bullets_th: Array.isArray(summary.bullets_th) ? summary.bullets_th.map(text).filter(Boolean).slice(0, 4) : [],
    bullets_en: Array.isArray(summary.bullets_en) ? summary.bullets_en.map(text).filter(Boolean).slice(0, 4) : [],
  };
}

function numberOrNull(value: unknown): number | null {
  const number = Number(value);
  return value !== null && value !== '' && Number.isFinite(number) ? number : null;
}

function firstImage(value: unknown): string {
  const first = rows(value)[0];
  if (first) return text(first.url) || text(first.image_url) || text(first.thumbnail_url);
  return typeof (value as unknown[])?.[0] === 'string' ? text((value as string[])[0]) : '';
}

function quote(value: Row): string {
  const raw = text(value.display_title) || text(value.title) || text(value.display_body) || text(value.comment) || text(value.details);
  const normalized = raw.replace(/\s+/g, ' ').trim();
  return normalized.length > 80 ? `${normalized.slice(0, 79).trimEnd()}…` : normalized;
}

function internalReview(value: Row): boolean {
  const identity = [value.expert_id, value.creator_id, value.expert_name, value.creator_name, value.aka_name, value.username]
    .map((item) => text(item).toLocaleLowerCase()).join(' ');
  return ['demo creator', 'master admin', 'system admin', 'expert_demo', 'creator_demo', 'masteradmin', '_e2e_', 'fixture']
    .some((token) => identity.includes(token));
}

function adaptExplorePlaces(raw: unknown): { places: LandingPlace[]; campaigns: LandingData['campaigns']; totalCampaigns: number } {
  const payload = (raw && typeof raw === 'object' ? raw : {}) as Row;
  const places = rows(payload.items).map((cafe, index): LandingPlace => {
    const location = cafe.location && typeof cafe.location === 'object' ? cafe.location as Row : {};
    const coordinates = Array.isArray(location.coordinates) ? location.coordinates : [];
    const distanceKm = numberOrNull(cafe.distance_km);
    const campaignId = text(cafe.campaign_id);
    const rewardPoints = Math.max(0, Number(cafe.campaign_reward_point) || 0);
    const hasCampaign = Boolean(campaignId && rewardPoints > 0);
    const rawRating = numberOrNull(cafe.rating);
    return {
      place_id: text(cafe.id) || text(cafe._id),
      name: text(cafe.name_local) || text(cafe.name) || text(cafe.name_en),
      lat: numberOrNull(coordinates[1]),
      lng: numberOrNull(coordinates[0]),
      distance_m: distanceKm === null ? null : Math.max(0, Math.round(distanceKm * 1000)),
      category: text(cafe.subcategory_slug) || text(cafe.category_slug),
      image_url: text(cafe.marker_image_url) || firstImage(cafe.featured_photos) || firstImage(cafe.beaw_photos) || firstImage(cafe.photos),
      rating: rawRating !== null && rawRating <= 5 ? rawRating : null,
      review_count: Math.max(0, Number(cafe.review_count) || 0),
      review_summary: storedReviewSummary(cafe.review_summary),
      rank: index + 1,
      selected: index === 0,
      has_campaign: hasCampaign,
      // `/cafes` returns only an eligible active campaign in these fields. It
      // intentionally omits inventory details from the public map payload, so
      // keep a positive availability sentinel for the shared marker component.
      campaign: hasCampaign ? { campaign_id: campaignId, reward_points: rewardPoints, remaining_slots: 1 } : null,
      tags: [...(Array.isArray(cafe.tags) ? cafe.tags : []), ...(Array.isArray(cafe.pin_keywords) ? cafe.pin_keywords : [])]
        .map(text).filter((tag, index, all) => tag && all.indexOf(tag) === index).slice(0, 4),
    };
  }).filter((place) => Boolean(place.place_id && place.name));
  const campaigns = places.flatMap((place) => place.campaign ? [{
    campaign_id: place.campaign.campaign_id,
    place_id: place.place_id,
    place_name: place.name,
    image_url: place.image_url,
    category: place.category,
    distance_m: place.distance_m,
    reward_points: place.campaign.reward_points,
    remaining_slots: place.campaign.remaining_slots,
    status: 'active',
  }] : []);
  return {
    places,
    campaigns,
    totalCampaigns: Math.max(campaigns.length, Number(payload.rewarded_nearby_place_count) || 0),
  };
}

async function loadExploreMap(city: string, coords?: { lat: number; lng: number }, category = 'coffee') {
  return adaptExplorePlaces(await apiRequest<unknown>('/cafes', {
    query: {
      city,
      ...(category ? { category } : {}),
      page: 1,
      page_size: 14,
      map_only: true,
      reviewed_only: true,
      include_campaigns: true,
      explore_preview: true,
      ...(coords ? { nearby: true, lat: coords.lat, lng: coords.lng } : {}),
    },
  }));
}

function mergeExploreMap(landing: LandingData, explore: Awaited<ReturnType<typeof loadExploreMap>> | null): LandingData {
  if (!explore?.places.length) return landing;
  return {
    ...landing,
    activity: {
      ...landing.activity,
      campaign_count: explore.totalCampaigns,
    },
    nearby_places: explore.places,
    campaigns: explore.campaigns,
  };
}

function adaptHomeIndex21(raw: unknown, city: string, coords?: { lat: number; lng: number }): LandingData {
  const home = (raw && typeof raw === 'object' ? raw : {}) as Row;
  const cafes = rows(home.cafes);
  const places: LandingPlace[] = cafes.slice(0, 12).map((cafe, index) => {
    const location = cafe.location && typeof cafe.location === 'object' ? cafe.location as Row : {};
    const coordinates = Array.isArray(location.coordinates) ? location.coordinates : [];
    const distanceKm = numberOrNull(cafe.distance_km);
    return {
      place_id: text(cafe.id) || text(cafe._id),
      name: text(cafe.name_local) || text(cafe.name) || text(cafe.name_en),
      lat: numberOrNull(coordinates[1]),
      lng: numberOrNull(coordinates[0]),
      distance_m: distanceKm === null ? null : Math.max(0, Math.round(distanceKm * 1000)),
      category: text(cafe.subcategory_slug) || text(cafe.category_slug),
      image_url: firstImage(cafe.featured_photos) || firstImage(cafe.beaw_photos) || firstImage(cafe.photos),
      rating: numberOrNull(cafe.rating) ?? numberOrNull(cafe.overall_score),
      review_count: Math.max(0, Number(cafe.review_count) || 0),
      review_summary: storedReviewSummary(cafe.review_summary),
      rank: index + 1,
      selected: index === 0,
      has_campaign: false,
      campaign: null,
    };
  }).filter((place) => Boolean(place.place_id && place.name));

  const placeById = new Map(places.map((place) => [place.place_id, place]));
  const community = home.community && typeof home.community === 'object' ? home.community as Row : {};
  const voices: CommunityVoice[] = rows(community.items)
    .filter((review) => !internalReview(review) && ['approved', 'published'].includes(text(review.status).toLowerCase() || 'approved'))
    .map((review) => {
      const placeId = text(review.cafe_id);
      const place = placeById.get(placeId);
      return {
        review_id: text(review.id) || text(review._id),
        place_id: placeId,
        place_name: place?.name || text(review.cafe_name),
        distance_m: place?.distance_m ?? null,
        quote: quote(review),
        image_url: firstImage(review.photos),
        reviewer: {
          display_name: text(review.aka_name) || text(review.expert_name) || text(review.creator_name) || text(review.username) || 'สมาชิก Beaw',
          avatar_url: text(review.avatar_url),
        },
      };
    })
    .filter((voice) => Boolean(voice.review_id && voice.place_id && voice.place_name && voice.quote))
    .slice(0, 3);

  return {
    location: { mode: 'home_index21_fallback', city, lat: coords?.lat ?? null, lng: coords?.lng ?? null, fallback_used: true },
    activity: { campaign_count: 0, generated_at: text(home.brain_generated_at) || new Date().toISOString() },
    nearby_places: places,
    community_voices: voices,
    campaigns: [],
    merchant_example: null,
    adaptive_blocks: rows(home.adaptive_blocks) as unknown as LandingAdaptiveBlock[],
  };
}

export async function loadLandingData(city: string, coords?: { lat: number; lng: number }, locale: 'th' | 'en' = 'th', category = 'coffee'): Promise<LandingData> {
  const query = { city, locale, ...(coords || {}) };
  const exploreRequest = loadExploreMap(city, coords, category).catch(() => null);
  let landing: LandingData;
  try {
    landing = await apiRequest<LandingData>('/public/landing', { query });
  } catch (reason) {
    if (!(reason instanceof ApiError) || reason.status !== 404) throw reason;
    const home = await apiRequest<unknown>('/home/index21', { query: { ...query, nearby: Boolean(coords) } });
    landing = adaptHomeIndex21(home, city, coords);
  }
  return mergeExploreMap(landing, await exploreRequest);
}

// Browser cache for the index: show the last payload instantly, and skip the
// network entirely while it is fresh (same 60 s the backend allows).
const LANDING_CACHE_PREFIX = 'beaw.landing.cache.v1:';
const LANDING_CACHE_FRESH_MS = 60_000;
const LANDING_CACHE_MAX_AGE_MS = 24 * 60 * 60_000;
const LANDING_CACHE_MAX_ENTRIES = 6;
const landingMemoryCache = new Map<string, { at: number; data: LandingData }>();

function landingCacheKey(city: string, coords: { lat: number; lng: number } | undefined, locale: string, category: string) {
  const point = coords ? `${coords.lat.toFixed(3)},${coords.lng.toFixed(3)}` : '';
  return `${LANDING_CACHE_PREFIX}${city.trim().toLowerCase()}|${point}|${locale}|${category}`;
}

export function readLandingCache(city: string, coords: { lat: number; lng: number } | undefined, locale: string, category: string): { data: LandingData; fresh: boolean } | null {
  const key = landingCacheKey(city, coords, locale, category);
  let entry = landingMemoryCache.get(key);
  if (!entry) {
    try { entry = JSON.parse(localStorage.getItem(key) || 'null') || undefined; } catch { entry = undefined; }
    if (entry) landingMemoryCache.set(key, entry);
  }
  if (!entry?.data || typeof entry.at !== 'number') return null;
  const age = Date.now() - entry.at;
  if (age > LANDING_CACHE_MAX_AGE_MS) return null;
  return { data: entry.data, fresh: age < LANDING_CACHE_FRESH_MS };
}

export function writeLandingCache(city: string, coords: { lat: number; lng: number } | undefined, locale: string, category: string, data: LandingData) {
  const key = landingCacheKey(city, coords, locale, category);
  const entry = { at: Date.now(), data };
  landingMemoryCache.set(key, entry);
  try {
    localStorage.setItem(key, JSON.stringify(entry));
    const keys = Object.keys(localStorage).filter(item => item.startsWith(LANDING_CACHE_PREFIX));
    if (keys.length > LANDING_CACHE_MAX_ENTRIES) {
      keys.map(item => { try { return [item, Number(JSON.parse(localStorage.getItem(item) || '{}').at) || 0] as const; } catch { return [item, 0] as const; } })
        .sort((a, b) => a[1] - b[1])
        .slice(0, keys.length - LANDING_CACHE_MAX_ENTRIES)
        .forEach(([item]) => localStorage.removeItem(item));
    }
  } catch { /* Storage full or blocked: the in-memory copy still helps this visit. */ }
}
